/** @module formatting */
import { ReactNode } from 'react';

let numeral = require( 'numeral' );

import { Moment } from 'moment';
import moment     from 'moment-timezone';

import _                                            from 'underscore';
import { parse, format, CountryCode, NumberFormat } from 'libphonenumber-js';
import { CONSTANTS }                                from '../constants';
import { TimeService }                 from '../services/TimeService';
import { ALPHABET, STRING_CONVERSION } from '../constants/FormatStringEnums';

const { FORMATTING: FormattingUtility } = CONSTANTS;

/* Converts a value to a certain type or returns the value */
export const convertValueToType: ( value: any, type: 'int' | 'float' | 'string' | 'currency' | 'phone' ) => any = ( value, type = null ) => {

	let typeMap = new Map( [
		    [ 'int', parseInt ],
		    [ 'float', parseFloat ],
		    [ 'string', String ],
		    [ 'currency', formatCurrency ],
		    [ 'phone', _.compose( sanitizePhoneNumber, formatPhoneNumber ) ],
	    ] ),
	    typeConversionFunc;

	if( _.isFunction( type ) ){

		typeConversionFunc = type as Function;

	} else if( _.isString( type ) && typeMap.has( type ) ){

		typeConversionFunc = typeMap.get( type ) as Function;

	} else {

		typeConversionFunc = ( value: any ) => value;
	}

	return typeConversionFunc( value );
};

/* Formats a value to currency format */
export const formatCurrency = ( value: number | string = '', withPrecision = true, roundedToUnit = false ) => {

	return withPrecision
	       ? roundedToUnit
	         ? numeral( value ).format( CONSTANTS.FORMATTING.MONEY.CURRENCY_UNIT )
	         : numeral( value ).format( CONSTANTS.FORMATTING.MONEY.CURRENCY )
	       : roundedToUnit
	         ? numeral( value ).format( CONSTANTS.FORMATTING.MONEY.CURRENCY_NO_PRECISION_UNIT )
	         : numeral( value ).format( CONSTANTS.FORMATTING.MONEY.CURRENCY_NO_PRECISION );
};

/* Formats a value to percentage string format */
export const formatPercentageString = ( value: number | string = '', withPrecision = true, scaleBy100 = false, withIndicator = false ) => {

	numeral.options.scalePercentBy100 = scaleBy100;

	let indicator = value == 0 ? null : value > 0 ? '+' : null;

	let formatValue = withPrecision
	                  ? numeral( value ).format( CONSTANTS.FORMATTING.MATH.PERCENTAGE )
	                  : numeral( value ).format( CONSTANTS.FORMATTING.MATH.PERCENTAGE_NO_PRECISION );

	return `${ ( withIndicator && indicator ) ? indicator : '' }${ formatValue }`;
};

/* Formats a value to the platform specific regional phone number format */
export const formatPhoneNumber = ( value = '' ) => {

	try{

		return format(
			value,
			FormattingUtility.COMMUNICATION.PHONEREGION as CountryCode,
			FormattingUtility.COMMUNICATION.PHONESTYLE as NumberFormat
		);

	} catch( e ) {

		//console.log( `${value} cannot be formatted into a phone number` );
	}

	return value;
};

/* Sanitizes a value to resemble a phone number's digit representation without formatting marks */
export const sanitizePhoneNumber = ( value = '' ) => {

	try{

		let nonDigits            = new RegExp( '\\D', 'g' ),
		    numberWithLeadingOne = new RegExp( '^(1)(\d{10})$' ),
		    stripped, sanitized;

		stripped = value
			.replace( nonDigits, '' )
			.replace( numberWithLeadingOne, '' );

		sanitized = parse( stripped, FormattingUtility.COMMUNICATION.PHONEREGION as unknown ).phone;

		return _.isUndefined( sanitized ) ? value : sanitized;

	} catch( e ) {

		//console.log( `${value} cannot be sanitized as a phone number.` );
	}

	return value;
};

export interface IFormatOptions {

	/* A group label for items to utilize HTML's OptionGroup with */
	group?: string,

	/* What key to attach the value onto */
	valueAttribute?: string,

	/* What key to attach the label onto */
	labelAttribute?: string,

	key?: string | number
}

export interface IFormatOptionValue {

	/* Unique key to utilize in components that require it. */
	key: string | number | boolean,

	/* Group label to display in option groups and use as a key to group options  by */
	group?: string,

	[ value: string ]: unknown,

}

export interface IGenericFormatOption extends IFormatOptionValue {
	value: string,
	text: string
}

interface IFormatOptionUtilFunction {( value: any, label: ReactNode | string | number | null, options?: IFormatOptions ): IFormatOptionValue}

/* Creates a formatted dropdown option object that can be used in an select component */
export const formatOption: IFormatOptionUtilFunction = ( value: any = null, label: any = '(no label)', _options: object = {} ) => {

	let options: IFormatOptions = {
		group:          null,
		valueAttribute: 'value',
		labelAttribute: 'text',
		..._options
	};

	let option = {
		key:                        value,
		[ options.valueAttribute ]: value,
		[ options.labelAttribute ]: label,
	};

	if( options.group ) option.group = options.group;
	if( options.key ) option.key = options.key;

	return option;
};

/* Capitalizes a string, optionally the first word only. */
export const capitalize = ( value = '', firstWordOnly = false, method: 'regex' | 'split' | 'complete' = 'split' ) => {

	if( !value || !_.isString( value ) ) return value;

	const methods = {
		regex: ( str ) => {
			return str.replace( /\w\S*/g, txt => txt.charAt( 0 ).toUpperCase() + txt.substr( 1 ).toLowerCase() );
		},

		split: ( str ) => {

			return [ ...str.split( ' ' ) ].map( w => w[ 0 ].toUpperCase() + w.substring( 1 ).toLowerCase() ).join( ' ' )
		},

		complete: ( str ) => {
			const mustLowers = [ 'a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor', 'as', 'at', 'by', 'for', 'from', 'in', 'into', 'near', 'of', 'on', 'onto', 'to', 'with' ];
			const mustUpper  = [ 'id', 'tv' ];
			const length     = str.length;

			return str.replace( /\b\w+/g, function( txt, offset ){
				const word = txt.toLowerCase();
				if( offset != 0 && offset != ( length - word.length ) && mustLowers.includes( word ) ){
					return word;
				} else if( mustUpper.includes( word ) ){
					return word.toUpperCase();
				} else {
					return word[ 0 ].toUpperCase() + word.substring( 1 );
				}
			} );
		}
	};

	if( firstWordOnly ){

		return value.charAt( 0 ).toUpperCase() + value.slice( 1 );

	} else {

		try{

			return methods[ method ]( value );

		} catch( e ) {

			return value;
		}
	}
};

/* uncapitalizes a string, first word only. */
export const uncapitalize = ( value = '' ) => {
	return value.charAt( 0 ).toLowerCase() + value.slice( 1 )
};

/* Removes surrounding space around a string */
export const trimString = ( value: string = '' ) => {

	if( !_.isString( value ) ) return '';

	try{

		return value.replace( /^\s\s*/, '' ).replace( /\s\s*$/, '' );

	} catch( e ) {
	}

	return value;
};

export const convertEmptyStringToNull = ( value: any = '' ) => {

	return ( value === '' ) ? null : value
}

export const convertNullToEmptyString = ( value: any = null ) => {

	return ( value === null ) ? '' : value
}

export const combineValuesByDelimiter = ( fields: Array<string | number> = [], delimiter = STRING_CONVERSION.DELIMITER ): string => {

	return fields.join( delimiter );
}

export const splitValuesByDelimiter = ( fields: string = '', delimiter = STRING_CONVERSION.DELIMITER ): string[] => {

	return fields.split( delimiter );
}

export const splitAndRecombineWithDelimiter = ( fields: string = '', combineDelimiter = STRING_CONVERSION.DELIMITER, splitDelimiter = STRING_CONVERSION.SPACE ): string => {

	return fields.split( splitDelimiter ).join( combineDelimiter );
}

/* Pads a value witha specific padding string */
export const padValue = ( n: string | number, width = 0, z: string = '0' ) => {

	n = n + '';

	return n.length >= width ? n : new Array( width - n.length + 1 ).join( z ) + n;
};

/* Rounds a value to a certain precision of decimals */
export const roundToPrecision = ( number: number, precision = 0 ) => {

	let shift = function( number: number, precision: number ){

		let numArray = ( "" + number ).split( "e" );

		return +( numArray[ 0 ] + "e" + ( numArray[ 1 ] ? ( +numArray[ 1 ] + precision ) : precision ) );
	};
	return shift( Math.round( shift( number, +precision ) ), -precision );
};

/* Formats a date object to the platform desired date display format */
export const formatDateString = ( dateTime = moment() ) => {

	return `${ dateTime.format( TimeService.displayDateFormat ) }`;
};

/* Formats a date object to a formal timestamp display format. Provides timezone option. */
export const formatDateTimeString = (
	dateTime         = moment(),
	timezone: string = null,
	timezoneFormat   = 'z'
) => {

	return (
		`${ dateTime.format( TimeService.displayDateFormat ) }` +
		`${ timezone ? ' ' + formatDateTimeZone( dateTime, timezone, timezoneFormat ) : '' }` +
		` at ${ dateTime.format( CONSTANTS.FORMATTING.TIME.TIME_MERIDIEM ) }`
	);
};

/* Formats a date object range to a formal timestamp display format. Provides timezone option. */
export const formatTimeRangeString = (
	startDateTime: Moment = moment(),
	endDateTime: Moment   = moment(),
	timezone: string      = null,
	nextDayIndicator      = '*'
) => {

	let start       = startDateTime.clone() as Moment,
	    end         = endDateTime.clone() as Moment,
	    startFormat = CONSTANTS.FORMATTING.TIME.TIME_MERIDIEM,
	    endFormat   = CONSTANTS.FORMATTING.TIME.TIME_MERIDIEM,
	    isNextDay   = end.isAfter( start, 'day' );

	if( start.get( 'minutes' ) == 0 ){
		startFormat = startFormat.replace( ':mm', '' );
	}
	if( end.get( 'minutes' ) == 0 ){
		endFormat = endFormat.replace( ':mm', '' );
	}
	if( start.format( `${ TimeService.displayDateFormat } a` ) == end.format( `${ TimeService.displayDateFormat } a` ) ){
		startFormat = startFormat.replace( 'a', '' );
	}

	if( _.isString( timezone ) ){

		if( end.tz( timezone ) != undefined ){
			start = start.tz( timezone );
			end   = end.tz( timezone );
			endFormat += ' (z)';
		}
	}

	return `${ start.format( startFormat ) } - ${ end.format( endFormat ) }${ isNextDay ? nextDayIndicator : '' }`;
};

/* Formats a date timezone */
export const formatDateTimeZone = ( dateTime = moment(), timezone: string = null, format = 'z' ) => {

	let dt = moment.isMoment( dateTime ) ? dateTime.clone() : moment( dateTime );

	if( dt.tz( timezone ) != undefined ){
		dt = dt.tz( timezone );
	}

	return `${ dt.format( format ) }`;
};

/* Formats a date object range to a formal date range display format. */
export const formatDateRangeString = ( startDateTime: string | Moment, endDateTime: string | Moment ) => {

	let start       = moment( startDateTime ),
	    end         = moment( endDateTime ),
	    startFormat = 'MMM D, YYYY',
	    endFormat   = 'MMM D, YYYY';

	if( start.month() === end.month() ){
		endFormat = endFormat.replace( 'MMM ', '' );
	}

	if( start.date() === end.date() ){
		endFormat = endFormat.replace( 'D, ', '' );
	}

	if( start.year() === end.year() ){
		startFormat = startFormat.replace( ', YYYY', '' );
	}

	return `${ start.format( startFormat ) } - ${ end.format( endFormat ) }`;
};

/* Sorts an object by it's keys */
export const sortObjectByKeys = ( objectToSort: Record<any, any> ): Record<any, any> => {

	return Object.keys( objectToSort ).sort().reduce( function( acc, key ){
		acc[ key ] = objectToSort[ key ];
		return acc;
	}, {} );
};

/* Sorts an object by it's values */
export const sortObjectByValues = ( objectToSort: Record<any, any>, reverse = false ) => {

	let sortedKeys = Object.keys( objectToSort )
	                       .sort( function( a, b ){
		                       return objectToSort[ a ] - objectToSort[ b ]
	                       } );

	if( reverse ){
		sortedKeys = sortedKeys.reverse();
	}

	return _.pick( objectToSort, sortedKeys );
};

/* Partitions an array by odd / even index number */
export const splitArrayOddEven = ( arrayToPartition: any[], unionSplit = false ) => {

	let partition = _.partition( arrayToPartition, ( item, index ) => {
		return index % 2;
	} );

	return unionSplit ? _.flatten( partition ) : partition;
};

/* Attemps to partitions an array by evenly based on length. Option available to then process into one array after sort as [ ...first half, ...second half ]. */
export const splitArrayEvenly = ( arrayToPartition: any[], unionSplit = false ) => {

	let len       = arrayToPartition.length,
	    limit     = Math.ceil( len / 2 ),
	    partition = _.partition( arrayToPartition, ( item, index ) => {
		    return index < limit;
	    } );

	return unionSplit ? _.flatten( partition ) : partition;
};

/* Creates one array from two, "zipping" indexes as alternating items from even array then odd array, eg: [ arr1_1, arr2_1, arr1_2, arr2_2, ... ] */
export const zipArraysOddEven = ( arr1: any[], arr2: any[] ) => {

	let len         = arr1.length + arr2.length,
	    newArr      = _.range( 0, len ),
	    arr1Counter = 0,
	    arr2Counter = 0;

	newArr = newArr.map( ( slot, index ) => {
		let value;

		if( !( index % 2 ) ){
			value = arr1[ arr1Counter ] ? arr1[ arr1Counter ] : null;
			arr1Counter++
		} else {
			value = arr2[ arr2Counter ] ? arr2[ arr2Counter ] : null;
			arr2Counter++
		}

		return value
	} );

	return _.compact<any[]>( newArr );
};

/* Slices into an array and relocates an item to a different index */
export const reindexItem = ( arrayToReindex: any[], oldIndex: number, newIndex: number ) => {

	if( newIndex >= arrayToReindex.length ){

		let k = newIndex - arrayToReindex.length + 1;

		while( k-- ){
			arrayToReindex.push( undefined );
		}
	}

	arrayToReindex.splice( newIndex, 0, arrayToReindex.splice( oldIndex, 1 )[ 0 ] );

	return arrayToReindex; // for testing
};

/* Converts a hex color into an RGBA space string */
export const hexToRGBa = ( color: string, alphaValue: string | number = 1 ) => {

	// accept hex with hash symbol
	if( typeof color === 'string' ){

		color = color.trim();

		if( color.substring( 0, 1 ) === '#' ){
			color = color.substring( 1 );
		}
	}

	// convert
	let r = parseInt( color.substring( 0, 2 ), 16 ),
	    g = parseInt( color.substring( 2, 4 ), 16 ),
	    b = parseInt( color.substring( 4, 6 ), 16 ),
	    a = typeof alphaValue == 'number' ? alphaValue : parseFloat( alphaValue );

	return ( `rgba(${ r }, ${ g }, ${ b }, ${ a })` );
};

export const HSLToHex = ( hsl: { h: number; s: number; l: number } ): string => {
	const { h, s, l } = hsl;

	const hDecimal = l / 100;
	const a        = ( s * Math.min( hDecimal, 1 - hDecimal ) ) / 100;
	const f        = ( n: number ) => {
		const k     = ( n + h / 30 ) % 12;
		const color = hDecimal - a * Math.max( Math.min( k - 3, 9 - k, 1 ), -1 );

		// Convert to Hex and prefix with "0" if required
		return Math.round( 255 * color )
		           .toString( 16 )
		           .padStart( 2, "0" );
	};
	return `#${ f( 0 ) }${ f( 8 ) }${ f( 4 ) }`;
}

export const intToHSL = ( int: number, saturation: number, lightness: number, asValues = false ): { h: number; s: number; l: number } | string => {

	let hue = int % 360;

	return asValues
	       ? { h: hue, s: saturation, l: lightness }
	       : `hsl(${ hue },${ saturation }%,${ lightness }%)`;
};

/* Converts a string into a hex color value. Good for strings that need to have their own "color" like usernames or categories. */
export const stringToColor = ( str: string, saturation = 100, lightness = 30, asHex = false ) => {

	const getHashCode = ( str: string ) => {

		let hash = 0;

		if( str.length === 0 ) return hash;

		for( let i = 0; i < str.length; i++ ){

			hash = str.charCodeAt( i ) + ( ( hash << 5 ) - hash );

			hash = hash & hash; // Convert to 32bit integer
		}
		return hash;
	};

	return asHex
	       ? HSLToHex( intToHSL( getHashCode( str ), saturation, lightness, true ) as { h: number; s: number; l: number } ) as string
	       : intToHSL( getHashCode( str ), saturation, lightness );
};

/* Converts a string into a hex color value based on a color map hash that uses the strings first letter. Defaults to black if not in the hash */
export const stringToColorLookup = ( str: string ) => {

	let lookupMap = new Map( [
		[ 'a', '#F8B195' ],
		[ 'b', '#F67280' ],
		[ 'c', '#C06C84' ],
		[ 'd', '#C5B7B6' ],
		[ 'e', '#C7D355' ],
		[ 'f', '#99B898' ],
		[ 'g', '#FECEAB' ],
		[ 'h', '#FF847C' ],
		[ 'i', '#9A589F' ],
		[ 'j', '#2A363B' ],
		[ 'k', '#A8A7A7' ],
		[ 'l', '#CC527A' ],
		[ 'm', '#88BE55' ],
		[ 'n', '#474747' ],
		[ 'o', '#04AE5E' ],
		[ 'p', '#45ADA8' ],
		[ 'q', '#547980' ],
		[ 'r', '#594F4F' ],
		[ 's', '#C8C8A9' ],
		[ 't', '#83AF9B' ],
		[ 'u', '#FE4365' ],
		[ 'v', '#FC9D9A' ],
		[ 'w', '#F9CDAD' ],
		[ 'x', '#A7226E' ],
		[ 'y', '#F26B38' ],
		[ 'z', '#2F9599' ],
	] );

	return ( str.length ) ? lookupMap.get( str.substr( 0, 1 ).toLowerCase() ) ?? '#000000' : '#000000';
};

export const generateVisualizationDataColors = ( extendedPalette = false ) => {

	// Visualization colors
	const coreVisualizationDataPalette = [
		'#182044',
		'#DA3E55',
		'#66C1C2',
		'#A0B261',
		'#F46621',
		'#843A7A',
		'#276A85',
		'#E3A6AF',
		'#7AEBB4',
		'#62A682',
		'#D8C70D',
		'#6435C9'
	];

	if( extendedPalette ){

		ALPHABET.map( letter => coreVisualizationDataPalette.push( stringToColorLookup( letter ) ) );
	}

	return coreVisualizationDataPalette;
}

/* Inspects if the string past in is a hex or rgba definition-like string */
export const stringIsColorDef = ( str: string ) => {

	return ( str.search( /^(rgba?|#)/i ) !== -1 )
}

/* Determine If A Color Is Bright Or Dark Using JavaScript
* https://awik.io/determine-color-bright-dark-using-javascript/ */
export const isColorDark = ( color: string ) => {

	// Variables for red, green, blue values
	let r, g, b, hsp;

	// Check the format of the color, HEX or RGB?
	if( color.match( /^rgb/ ) ){

		// If RGB --> store the red, green, blue values in separate variables
		let RGBColor = color.match( /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/ );

		r = RGBColor[ 1 ];
		g = RGBColor[ 2 ];
		b = RGBColor[ 3 ];

	} else {

		// If hex --> Convert it to RGB: http://gist.github.com/983661
		let HexColor = +( "0x" + color.slice( 1 ).replace(
			color.length < 5 && /./g, '$&$&' ) );

		r = HexColor >> 16;
		g = HexColor >> 8 & 255;
		b = HexColor & 255;
	}

	// HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
	hsp = Math.sqrt(
		0.299 * ( r * r ) +
		0.587 * ( g * g ) +
		0.114 * ( b * b )
	);

	// Using the HSP value, determine whether the color is light or dark
	return hsp <= 127.5;
}

/* Adjust a color's brightness
* https://stackoverflow.com/questions/5560248/programmatically-lighten-or-darken-a-hex-color-or-rgb-and-blend-colors#answer-37600815*/
export function adjustBrightness( col, amt ){

	var usePound = false;

	if( col[ 0 ] == "#" ){
		col      = col.slice( 1 );
		usePound = true;
	}

	var R = parseInt( col.substring( 0, 2 ), 16 );
	var G = parseInt( col.substring( 2, 4 ), 16 );
	var B = parseInt( col.substring( 4, 6 ), 16 );

	// to make the colour less bright than the input
	// change the following three "+" symbols to "-"
	R = R + amt;
	G = G + amt;
	B = B + amt;

	if( R > 255 ){
		R = 255;
	} else if( R < 0 ) R = 0;

	if( G > 255 ){
		G = 255;
	} else if( G < 0 ) G = 0;

	if( B > 255 ){
		B = 255;
	} else if( B < 0 ) B = 0;

	var RR = ( ( R.toString( 16 ).length == 1 ) ? "0" + R.toString( 16 ) : R.toString( 16 ) );
	var GG = ( ( G.toString( 16 ).length == 1 ) ? "0" + G.toString( 16 ) : G.toString( 16 ) );
	var BB = ( ( B.toString( 16 ).length == 1 ) ? "0" + B.toString( 16 ) : B.toString( 16 ) );

	return ( usePound ? "#" : "" ) + RR + GG + BB;

}
